import {
  dataByResearchInsufficiencyReason,
  ResearchInsufficiencyReason,
} from "@/constants/terminal/research-insufficiency-reason"

// The console's one mark vocabulary, shared by the cohort matrix, the Research
// Category rail and one Company's Research Tasks table. Hue answers one question
// only: does this block Research Sufficiency. The fill treatment carries what
// kind of non-answer it is, so a Source nobody could search never reads as a
// successful Source Check. The matrix's progress bar has no room for fill
// treatments, so each mark also names the plain fill of its bar segment.
export enum ResearchMark {
  CHECKED = "checked",
  CHECKED_WITH_RECORDS = "checked_with_records",
  BLOCKED = "blocked",
  FAILED = "failed",
  PARTIAL = "partial",
  BOUND_REACHED = "bound_reached",
  STALE = "stale",
  MISSING = "missing",
  RUNNING = "running",
  QUEUED = "queued",
  UNCAPTURED = "uncaptured",
  NO_SOURCE = "no_source",
}

const reasonLabel = (reason: ResearchInsufficiencyReason) =>
  dataByResearchInsufficiencyReason[reason].label

export const dataByResearchMark: Record<
  ResearchMark,
  { className: string; barClassName: string; label: string; severity: number }
> = {
  [ResearchMark.FAILED]: {
    className: "bg-bad-foreground",
    barClassName: "bg-bad-foreground",
    label: reasonLabel(ResearchInsufficiencyReason.FAILED),
    severity: 0,
  },
  [ResearchMark.BLOCKED]: {
    className: "bg-bad-foreground",
    barClassName: "bg-bad-foreground",
    label: reasonLabel(ResearchInsufficiencyReason.BLOCKED),
    severity: 1,
  },
  [ResearchMark.BOUND_REACHED]: {
    className: "border-caution-foreground border-2",
    barClassName: "bg-caution-foreground",
    label: reasonLabel(ResearchInsufficiencyReason.BOUND_REACHED),
    severity: 2,
  },
  // Kept the records it read; its search never finished. Half filled, so it
  // never depends on telling a dark caution hue from the background.
  [ResearchMark.PARTIAL]: {
    className:
      "border-caution-foreground border bg-[image:linear-gradient(to_top,hsl(var(--caution-foreground))_50%,transparent_50%)]",
    barClassName: "bg-caution-foreground",
    label: reasonLabel(ResearchInsufficiencyReason.PARTIAL),
    severity: 3,
  },
  // Searched successfully once, but too long ago to count as proof now.
  [ResearchMark.STALE]: {
    className: "border-caution-foreground border-2 border-dashed",
    barClassName: "bg-caution-foreground/60",
    label: reasonLabel(ResearchInsufficiencyReason.STALE),
    severity: 4,
  },
  [ResearchMark.MISSING]: {
    className: "border-muted-foreground/50 border-2",
    barClassName: "bg-muted-foreground/60",
    label: reasonLabel(ResearchInsufficiencyReason.MISSING),
    severity: 5,
  },
  // A running Company Run's live states: its Source Check has started and has
  // no outcome yet, or its Research Task is due and has no Check in this run.
  // They rank after every non-answer, so a failure still leads a strip.
  [ResearchMark.RUNNING]: {
    className: "bg-terminal-accent motion-safe:animate-pulse",
    barClassName: "bg-terminal-accent motion-safe:animate-pulse",
    label: "Running",
    severity: 6,
  },
  [ResearchMark.QUEUED]: {
    className: "border-terminal-accent/60 border",
    barClassName: "bg-terminal-accent/30",
    label: "Queued",
    severity: 7,
  },
  // Deliberately colourless: no implemented capture is a disclosure, not an
  // outcome.
  [ResearchMark.UNCAPTURED]: {
    className:
      "border-muted-foreground/50 border bg-[image:repeating-linear-gradient(45deg,hsl(var(--muted-foreground))_0_1px,transparent_1px_4px)]",
    barClassName: "bg-muted-foreground/30",
    label: "Not currently supported",
    severity: 8,
  },
  [ResearchMark.NO_SOURCE]: {
    className: "border-muted-foreground/50 border-2 border-dotted",
    barClassName: "bg-muted-foreground/30",
    label: "No Source in this jurisdiction",
    severity: 9,
  },
  // A successful search that found records carries a dot, so a clean search
  // and one with something to read are told apart at a glance.
  [ResearchMark.CHECKED_WITH_RECORDS]: {
    className:
      "bg-good-foreground bg-[image:radial-gradient(circle,hsl(var(--background))_22%,transparent_27%)]",
    barClassName:
      "bg-good-foreground bg-[image:radial-gradient(circle,hsl(var(--background))_1.5px,transparent_2px)] bg-[length:8px_8px] bg-center bg-no-repeat",
    label: "Checked, records found",
    severity: 10,
  },
  [ResearchMark.CHECKED]: {
    className: "bg-good-foreground",
    barClassName: "bg-good-foreground",
    label: "Checked, no records",
    severity: 11,
  },
}

// Either successful search: it answers the Research Task.
export const checkedMarks = new Set([
  ResearchMark.CHECKED,
  ResearchMark.CHECKED_WITH_RECORDS,
])
